/**
 * L'heure du bureau. **Le seul fichier du bureau qui appelle `new Date()`** : partout ailleurs,
 * l'heure vient d'une `Clock` reçue en paramètre, sans quoi rien n'est reproductible.
 *
 * Tout est formaté en `Europe/Paris`, jamais dans le fuseau de la machine : une classe travaille à
 * l'heure de Paris même si l'ordinateur du fond de la salle est resté en UTC.
 */

export const PARIS_TIME_ZONE = 'Europe/Paris';

const TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  timeZone: PARIS_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
});

const DATE_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  timeZone: PARIS_TIME_ZONE,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/** `en-CA` donne `AAAA-MM-JJ` : la seule locale dont le format court est déjà trié. */
const ISO_DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: PARIS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** « 14:32 » — l'horloge de la barre de menu, en chiffres alignés (`font-variant-numeric`). */
export function formatParisTime(date: Date): string {
  return TIME_FORMAT.format(date);
}

/** « lun. 7 sept. » */
export function formatParisDate(date: Date): string {
  return DATE_FORMAT.format(date);
}

/**
 * Numéro de jour : le nombre de journées entières écoulées depuis le 1ᵉʳ janvier 1970, **compté à
 * Paris**. Ce n'est pas une date, c'est un compteur : la différence entre deux appels donne un
 * nombre de jours exact, sans piège d'heure d'été. C'est ce dont une app a besoin quand elle
 * demande « combien de jours depuis ? » — et rien de plus n'est inventé ici.
 */
export function parisDayNumber(date: Date): number {
  const [year, month, day] = ISO_DAY_FORMAT.format(date).split('-').map(Number);
  return Math.floor(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1) / 86_400_000);
}

/** Les morceaux d'une date lue **à Paris** — la brique de `parisLocalToIso` ci-dessous. */
const PARIS_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: PARIS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Le décalage de Paris (en millisecondes) à un instant donné : +1 h en hiver, +2 h en été. Lu du
 * fuseau lui-même par comparaison — jamais écrit en dur, sans quoi le passage à l'heure d'été
 * décalerait toutes les échéances d'une heure, une fois par an, sans que rien ne le signale. */
function parisOffsetMs(utcMs: number): number {
  const parts = Object.fromEntries(
    PARIS_PARTS.formatToParts(new Date(utcMs))
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)]),
  ) as Record<string, number>;
  const wall = Date.UTC(
    parts.year ?? 1970,
    (parts.month ?? 1) - 1,
    parts.day ?? 1,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
  );
  return wall - utcMs;
}

/**
 * Convertit ce qu'un `<input type="datetime-local">` renvoie (`2026-09-07T17:30`, une heure de
 * mur SANS fuseau) en instant ISO complet, **interprété à Paris**.
 *
 * Pourquoi ce détour plutôt que `new Date(value).toISOString()` : le constructeur lirait cette
 * chaîne dans le fuseau de la MACHINE. Un poste de la salle resté en UTC enverrait alors 17 h 30
 * UTC, soit 19 h 30 à Paris — l'échéance affichée à la classe serait fausse de deux heures, et
 * personne ne s'en apercevrait avant la séance. Le bureau formate déjà tout à l'heure de Paris
 * (voir le commentaire de tête) ; ce qu'un enseignant saisit doit suivre la même règle.
 *
 * Deux passes : la première estime le décalage à l'heure de mur prise pour de l'UTC, la seconde le
 * corrige à l'instant réellement obtenu. C'est ce qui rend le résultat juste **de part et d'autre
 * d'un changement d'heure**, où le décalage de départ et celui d'arrivée diffèrent.
 *
 * `null` quand la chaîne n'a pas la forme attendue : l'appelant affiche alors un message qui dit
 * quoi faire, jamais une échéance inventée.
 */
export function parisLocalToIso(local: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local);
  if (match === null) return null;
  const [, year, month, day, hour, minute, second = '0'] = match;
  const wall = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );
  const utc = wall - parisOffsetMs(wall - parisOffsetMs(wall));
  if (!Number.isFinite(utc)) return null;
  const expected = [year, month, day, hour, minute, second].map(Number);
  const parts = Object.fromEntries(
    PARIS_PARTS.formatToParts(new Date(utc)).map((part) => [part.type, part.value]),
  );
  const actual = ['year', 'month', 'day', 'hour', 'minute', 'second'].map((part) =>
    Number(parts[part]),
  );
  // Reject invalid dates and the nonexistent hour at the spring DST transition.
  return expected.every((part, index) => part === actual[index])
    ? new Date(utc).toISOString()
    : null;
}

export interface Clock {
  now(): Date;
  day(): number;
}

/** Construit une horloge. `source` est injectable pour que rien d'autre n'ait besoin de
 * `new Date()`. */
export function createClock(source: () => Date = () => new Date()): Clock {
  return {
    now: () => source(),
    day: () => parisDayNumber(source()),
  };
}

/** Valeur d’un champ datetime-local à Paris. */
export function parisInputValue(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  return `${ISO_DAY_FORMAT.format(date)}T${TIME_FORMAT.format(date)}`;
}
